<template>
    <div class="flex items-center gap-1">
        <span :class="displayValueClass">{{ displayValue }}</span>
        <DsIconButton
            v-if="isToggleable"
            :icon="['far', isMasked ? 'fa-eye-slash' : 'fa-eye']"
            size="xs"
            @click="isMasked = !isMasked"
        />
    </div>
</template>

<script setup lang="ts">
import DsIconButton from '@/components/design-system/button/DsIconButton.vue';
import {
    formatAccountNumber,
    formatEIN,
    formatSSN,
    getMaskedValue,
    SECURITY_CHARACTER,
} from '@ds/form/input/text/text-input-mask';
import { MaskType } from '@ds/form/input/text/text-input.types';
import { computed, ref } from 'vue';

type DisplayMode = 'fixed' | 'dynamic';

interface DsMaskedDisplayProps {
    value: string;
    maskType: MaskType;
    numVisibleCharacters?: number;
    isToggleable?: boolean;
    displayMode?: DisplayMode;
    displayValueClass?: string;
}

const props = withDefaults(defineProps<DsMaskedDisplayProps>(), {
    numVisibleCharacters: 4,
    isToggleable: false,
    displayMode: 'fixed',
    displayValueClass: 'text-base font-semibold',
});

const isMasked = ref(true);

const displayValue = computed(() => {
    const formattedValue = formatValueWithSeparators(props.value, props.maskType);

    if (isMasked.value) {
        if (props.displayMode === 'fixed') {
            const visibleChars = formattedValue.slice(-props.numVisibleCharacters);
            const securityChars = SECURITY_CHARACTER.repeat(4);
            return securityChars + ' ' + visibleChars;
        } else {
            return getMaskedValue(formattedValue, props.maskType, props.numVisibleCharacters);
        }
    }

    return formattedValue;
});

const formatValueWithSeparators = (value: string, maskType: MaskType): string => {
    switch (maskType) {
        case MaskType.SSN:
            return formatSSN(value);
        case MaskType.EIN:
            return formatEIN(value);
        case MaskType.ACCOUNT_NUMBER:
            return formatAccountNumber(value);
        default:
            return value;
    }
};
</script>
